import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
} from "@mui/material";
import axios from "axios";
import * as React from "react";
import {Config} from "../../config/config";
import {Helpers} from "../../utils/helpers";
import {PopupMessage} from "../../utils/message";

export type UpdateRubyType = "ADDING" | "SUBTRACTING";

interface PlayerUpdateRubyProps {
    playerId: string;
    currentRuby?: number | null;
}

interface PlayerUpdateRubyState {
    open: boolean;
    loading: boolean;
    type: UpdateRubyType;
    amount: number;
    description: string;
}

const rubyActionOptions: Array<{label: string; value: UpdateRubyType}> = [
    {label: "Add Ruby", value: "ADDING"},
    {label: "Subtract Ruby", value: "SUBTRACTING"},
];

export class PlayerUpdateRuby extends React.Component<PlayerUpdateRubyProps, PlayerUpdateRubyState> {
    constructor(props: PlayerUpdateRubyProps) {
        super(props);

        this.state = {
            open: false,
            loading: false,
            type: "ADDING",
            amount: 0,
            description: "",
        };
    }

    openModal = () => {
        this.setState({
            open: true,
            type: "ADDING",
            amount: 0,
            description: "",
        });
    };

    closeModal = () => {
        if (!this.state.loading) {
            this.setState({open: false});
        }
    };

    onChangeType = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({type: e.target.value as UpdateRubyType});
    };

    onChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({amount: Number(e.target.value)});
    };

    onChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({description: e.target.value});
    };

    submit = async () => {
        const {type, amount, description} = this.state;

        if (!Number.isFinite(amount) || amount <= 0) {
            PopupMessage.error("Amount must be greater than 0");
            return;
        }

        this.setState({loading: true});

        try {
            const response = await axios.put(
                `${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/ruby`,
                {
                    amount,
                    type,
                    description,
                },
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                },
            );

            if (response.data?.success) {
                const offlineActionId = response.data?.data?.offlineActionId;
                PopupMessage.success(
                    offlineActionId
                        ? `Offline action created. Ruby will update when the user logs in. (ID: ${offlineActionId})`
                        : "Ruby update queued successfully",
                );

                this.setState({
                    open: false,
                    loading: false,
                    type: "ADDING",
                    amount: 0,
                    description: "",
                });
            } else {
                PopupMessage.error("Failed to update ruby");
                this.setState({loading: false});
            }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || "Failed to update ruby";
            PopupMessage.error(message);
            this.setState({loading: false});
        }
    };

    render() {
        const {currentRuby} = this.props;
        const {open, loading, type, amount, description} = this.state;

        return (
            <React.Fragment>
                {!Helpers.isProd() && (
                    <Button
                        disableElevation
                        onClick={this.openModal}
                        variant="contained"
                        size="small"
                    >
                        Update Ruby
                    </Button>
                )}

                <Dialog
                    open={open}
                    onClose={this.closeModal}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Update User Ruby</DialogTitle>
                    <DialogContent dividers>
                        {typeof currentRuby === "number" && (
                            <div style={{marginBottom: 12}}>
                                Current Ruby: <b>{currentRuby.toLocaleString()}</b>
                            </div>
                        )}
                        {currentRuby == null && (
                            <div style={{marginBottom: 12}}>
                                Current Ruby: <b>Not linked</b>
                            </div>
                        )}
                        <TextField
                            select
                            fullWidth
                            label="Action"
                            value={type}
                            onChange={this.onChangeType}
                            margin="normal"
                        >
                            {rubyActionOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            required
                            // type="number"
                            label="Amount"
                            value={amount}
                            onChange={this.onChangeAmount}
                            margin="normal"
                            // inputProps={{min: 1}}
                        />
                        <TextField
                            fullWidth
                            label="Description (optional)"
                            value={description}
                            onChange={this.onChangeDescription}
                            margin="normal"
                            inputProps={{maxLength: 250}}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button disableElevation onClick={this.closeModal} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            disableElevation
                            onClick={this.submit}
                            color="primary"
                            variant="contained"
                            disabled={loading}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}
